import { Alert, Badge, Flex, Group, Paper, Progress, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AbsenceMotivation,
  TravelActivityType,
  TravelDayBalancePreview,
  TravelOwnDayBalance,
  absenceMotivationTakesLeave,
  travelCapDaysBlockSubmit,
  travelMinimumLeaveDays,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { getTravelOwnDayBalanceOptions } from '../../model/TravelRequestQuery';
import { travelDayBalanceColor, travelFormationActivityTypes } from '../TravelDayBalanceCommon';

interface TravelDayBalanceCardProps {
  preview: TravelDayBalancePreview | undefined;
  isLoading: boolean;
  isError: boolean;
  activityType: TravelActivityType;
  absenceMotivation: AbsenceMotivation | null;
  presentsPaper?: boolean | null;
}

interface Pool {
  used: number;
  requested: number;
  cap: number;
  // A request blocked for passing any cap charges none of its days, in whichever pool and year they fall.
  refused?: boolean;
}

interface YearPools {
  year: number;
  withPaper: Pool;
  professionalDevelopment: Pool;
}

function fromPreview(preview: TravelDayBalancePreview, blocked: boolean): YearPools[] {
  return preview.years.map(year => ({
    year: year.year,
    withPaper: {
      used: year.withPaperUsed,
      requested: preview.consumes ? year.thisRequestPaidDays : 0,
      cap: year.withPaperCap,
      refused: blocked && year.thisRequestPaidDays > 0,
    },
    professionalDevelopment: {
      used: year.professionalDevelopmentUsed,
      requested: preview.consumes ? year.thisRequestProfessionalDevelopmentDays : 0,
      cap: year.professionalDevelopmentCap,
      refused: blocked && year.thisRequestProfessionalDevelopmentDays > 0,
    },
  }));
}

function fromOwnBalance(balance: TravelOwnDayBalance): YearPools[] {
  return [
    {
      year: balance.year,
      withPaper: { used: balance.withPaperUsed, requested: 0, cap: balance.withPaperCap },
      professionalDevelopment: {
        used: balance.professionalDevelopmentUsed,
        requested: 0,
        cap: balance.professionalDevelopmentCap,
      },
    },
  ];
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  const labelId = useId();

  return (
    <Stack
      gap={2}
      miw={0}
      role="group"
      aria-labelledby={labelId}
    >
      {/* One lg line tall, so a short tag or note leaves every label on the same top. */}
      <Flex
        mih={28}
        align="center"
      >
        {children}
      </Flex>
      <Text
        id={labelId}
        size="xs"
        c="dimmed"
      >
        {label}
      </Text>
    </Stack>
  );
}

interface PoolBlockProps extends Pool {
  title: string;
  consumes: boolean;
}

function PoolBlock({ title, consumes, used, requested, cap, refused }: PoolBlockProps) {
  const { t } = useTranslation();
  const titleId = useId();
  // Days the cap refuses are never charged, so the totals stay at what is already used.
  const total = refused ? used : used + requested;
  const over = refused ? 0 : total - cap;

  return (
    <Stack
      gap="xs"
      role="group"
      aria-labelledby={titleId}
    >
      <Text
        id={titleId}
        size="sm"
        fw="500"
      >
        {title}
      </Text>
      <SimpleGrid
        cols={3}
        spacing="xs"
      >
        <Tile label={t('travel.requests.form.dayBalance.used')}>
          <Text
            size="lg"
            fw="500"
          >
            {used}
          </Text>
        </Tile>
        <Tile label={t('travel.requests.form.dayBalance.thisRequest')}>
          {consumes && refused && (
            <Group
              gap={6}
              wrap="nowrap"
            >
              <Text
                size="lg"
                fw="500"
                c="red"
                td="line-through"
              >
                {requested}
              </Text>
              <Text
                size="xs"
                c="red"
              >
                {t('travel.requests.form.dayBalance.notGrantable')}
              </Text>
            </Group>
          )}
          {consumes && !refused && (
            <Text
              size="lg"
              fw="500"
            >
              {requested}
            </Text>
          )}
          {!consumes && (
            <Text
              size="xs"
              c="dimmed"
            >
              {t('travel.requests.form.dayBalance.notConsuming')}
            </Text>
          )}
        </Tile>
        <Tile label={t('travel.requests.form.dayBalance.remaining')}>
          {over > 0 ? (
            <Badge
              color="red"
              variant="light"
              size="xs"
              tt="none"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('travel.requests.form.dayBalance.over', { over })}
            </Badge>
          ) : (
            <Text
              size="lg"
              fw="500"
            >
              {Math.max(cap - total, 0)}
            </Text>
          )}
        </Tile>
      </SimpleGrid>
      <Group
        gap="xs"
        wrap="nowrap"
      >
        {cap > 0 && (
          <Progress
            flex={1}
            value={Math.min((total / cap) * 100, 100)}
            color={travelDayBalanceColor(total, cap)}
            size="sm"
            aria-label={title}
          />
        )}
        <Text size="sm">{`${total} / ${cap}`}</Text>
      </Group>
    </Stack>
  );
}

export function TravelDayBalanceCard(props: TravelDayBalanceCardProps) {
  const { preview, isLoading, isError, activityType, absenceMotivation, presentsPaper } = props;
  const { t } = useTranslation();
  // A failed preview must not fall back to the current year, which may not be the trip's year.
  const needsOwnBalance = !isLoading && !isError && (preview == null || preview.years.length === 0);
  const ownBalance = useQuery({
    ...getTravelOwnDayBalanceOptions(new Date().getFullYear()),
    enabled: needsOwnBalance,
    retry: false,
  });

  const exceeds =
    preview?.consumes === true &&
    preview.years.some(year => year.exceedsWithPaper || year.exceedsProfessionalDevelopment);
  // Under WARN the backend accepts a request over the cap and only flags it.
  const blocked =
    exceeds && travelCapDaysBlockSubmit(preview?.enforcement.dayCap, absenceMotivation, activityType, presentsPaper);
  const uncovered = preview == null ? 0 : Math.max(preview.workingDays - preview.leaveDays, 0);
  const missingLeaveDays = preview?.capUsableDays == null ? 0 : Math.max(uncovered - preview.capUsableDays, 0);

  let years: YearPools[] = [];
  if (preview != null && preview.years.length > 0) {
    years = fromPreview(preview, blocked);
  } else if (ownBalance.data != null) {
    years = fromOwnBalance(ownBalance.data);
  }
  const pending = isLoading || (needsOwnBalance && ownBalance.isPending);
  const failed = isError || (needsOwnBalance && ownBalance.isError);
  const consumes = preview?.consumes !== false;

  return (
    <Paper
      withBorder
      p="sm"
      role="group"
      aria-label={t('travel.requests.form.dayBalance.title')}
    >
      <Stack gap="sm">
        <Text
          size="sm"
          fw="500"
        >
          {t('travel.requests.form.dayBalance.title')}
        </Text>
        {failed && <Alert color="red">{t('travel.requests.dayBalance.error')}</Alert>}
        {pending ? (
          <Skeleton
            height={16}
            width="60%"
            data-testid="travel-day-balance-loading"
          />
        ) : (
          !failed && (
            <>
              {exceeds && blocked && (
                <Text
                  size="sm"
                  fw="500"
                  c="red"
                  role="alert"
                >
                  {t('travel.requests.form.dayBalance.exceedsBlocked')}
                  {missingLeaveDays > 0 &&
                    `; ${t('travel.requests.form.daySum.coverMore', { count: missingLeaveDays })}`}
                  .
                </Text>
              )}
              {exceeds && !blocked && (
                <Text
                  size="sm"
                  fw="500"
                  c="orange"
                  role="alert"
                >
                  {t('travel.requests.form.dayBalance.exceedsFlagged')}
                </Text>
              )}
              {years.map(({ year, withPaper, professionalDevelopment }) => (
                <Stack
                  key={year}
                  gap="sm"
                >
                  <PoolBlock
                    title={t('travel.requests.form.dayBalance.withPaperYear', { year })}
                    consumes={consumes}
                    {...withPaper}
                  />
                  {travelFormationActivityTypes.includes(activityType) && (
                    <PoolBlock
                      title={t('travel.requests.form.dayBalance.professionalDevelopmentYear', { year })}
                      consumes={consumes}
                      {...professionalDevelopment}
                    />
                  )}
                </Stack>
              ))}
              {preview != null && (
                <Text
                  size="xs"
                  c="dimmed"
                >
                  {t('travel.requests.form.dayBalance.workingDays', { workingDays: preview.workingDays })}
                </Text>
              )}
              {preview != null && absenceMotivationTakesLeave(absenceMotivation) && (
                <Text
                  size="xs"
                  c="dimmed"
                >
                  {t('travel.requests.form.dayBalance.leaveBound', {
                    minimum:
                      absenceMotivation === 'BEYOND_CAP_ON_LEAVE'
                        ? travelMinimumLeaveDays(preview)
                        : preview.minimumLeaveDays,
                    maximum: preview.workingDays,
                  })}
                </Text>
              )}
            </>
          )
        )}
      </Stack>
    </Paper>
  );
}
